'use client'

import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoginSchema } from '@/schemas'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { login } from '@/actions/auth/login'
import { useEffect, useState, useTransition } from 'react'
import { Button } from '@/components/ui/button'
import { FloatingLabelInput } from '@/components/ui/floating-input'
import { toast } from 'sonner'
import Image from 'next/image'
import Link from 'next/link'
import { signOut } from 'next-auth/react'
import { useIsMobile } from '@/hooks/use-mobile'
import { useTheme } from '@/views/shared/layout/theme-provider'

interface Props {
  title: string
  base64Logo?: string
}

export const LoginForm = ({ title, base64Logo }: Props) => {
  const [isPending, startTransition] = useTransition()

  const isMobile = useIsMobile()
  const [mounted, setMounted] = useState(false)
  const { theme } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { userid: '', password: '' }
  })

  const onSubmit = (values: z.infer<typeof LoginSchema>) => {
    startTransition(async () => {
      await signOut({ redirect: false })
      const signInData = await login(values)
      if (signInData?.error) toast.error(signInData.error)
    })
  }

  const getThemeMode = () => {
    if (!mounted) return 'light' // SSR safe: 최초엔 무조건 light
    if (theme === 'dark') return 'dark'
    if (theme === 'system') {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
      return 'light'
    }
    return 'light'
  }

  const themeMode = getThemeMode()

  return (
    <div className='flex h-full w-full'>
      {/* 왼쪽 패널 */}
      <div className='relative w-full lg:w-1/3 h-full p-8 bg-card'>
        <header className='absolute top-8 left-8'>
          <Link href='/login' className='flex items-center cursor-pointer'>
            {base64Logo && base64Logo !== '' ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={base64Logo} alt='logo' className='w-[25px] h-[25px]' />
            ) : (
              <Image height={25} width={25} alt='logo' src='/logo.png' />
            )}
            <span className='ml-2 text-2xl font-bold text-foreground'>
              {title || 'Insight Portal'}
            </span>
          </Link>
        </header>
        <div className='flex h-full flex-col justify-center'>
          <div className='mx-auto w-full max-w-sm'>
            <div className='mb-6'>
              <h2 className='text-2xl font-semibold mb-1'>Welcome! 👋</h2>
              <p className='text-muted-foreground text-sm'>Please sign-in to your account</p>
            </div>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                <FormField
                  control={form.control}
                  name='userid'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label='Email or Username'
                          className='w-full'
                          {...field}
                          disabled={isPending}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label='Password'
                          type='password'
                          className='w-full'
                          {...field}
                          disabled={isPending}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button variant='color' type='submit' className='w-full' disabled={isPending}>
                  LOGIN
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* 오른쪽 패널 */}
      {!isMobile && (
        <div className='w-2/3 h-full flex items-center justify-center bg-background relative'>
          {/* 일러스트 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={`/auth-login-illustration-${themeMode}.png`}
            alt='auth-login-illustration-light'
            sizes='100vw'
            className='max-w-[60vh] px-4 h-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10'
          />
          {/* 그림자 마스크: 오른쪽 하단에 부착 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={`/auth-cover-login-mask-${themeMode}.png`}
            alt='shadow mask'
            className='absolute top-[66%] left-1/2 -translate-x-1/2 w-full'
          />
        </div>
      )}
    </div>
  )
}
